/**
 * Top Navigation Bar for Store Management
 * Provides navigation functionality between store and organization contexts
 * with support for draft mode and scope selection
 *
 * @module components/TopNavigationBarStores
 */
import { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logger } from '@parrot-software/observability';

// MUI Components
import Box from '@mui/material/Box';

// Providers & Hooks
import { useSidebarLayout } from 'providers/root/SidebarLayoutProvider';
import useAmplitude from 'providers/root/AmplitudeProvider/useAmplitude';
import { config as amplitudeConfig } from 'providers/root/AmplitudeProvider/events';
import { setUserPropertyRequest } from 'providers/root/AmplitudeProvider/helpers';

// Navigation Components
import TopNavigationBar from '@experimental-components/TopNavigationBar';
import { NavigationType } from '@experimental-components/TopNavigationBar/types';

// Feature Management
import { useFeatures } from 'FeatureProvider';
import { useUser } from 'containers/Users';

// Store & Navigation Hooks
import { StoreProps } from 'hooks/navigation/interfaces';
import useUserStore from 'hooks/useUserStore';
import useOrganization from 'hooks/Organization/useOrganization';
import useNavigation from 'hooks/navigation/useNavigation';
import useLicenseState from 'hooks/useLicenseState';
import useMenuMakerDrawerState from 'hooks/useMenuMakerDrawerState';
import { useMenuScopeNavigation, useNavbarScopeStyles, useMenuScopeSync } from 'hooks/menuScope';

// Utilities
import history from 'utils/history';
import { getValidateItemsAvailabilityFlags } from 'utils/featureFlags/menus';

// Local Components
import { CustomStoreIcon, CustomOrganizationIcon } from '@experimental-components/IconsComponents/CustomIcons';

import { MENU_ROUTES } from 'utils/menuMaker/constants';

import Button from './ButtonNav';
import BannerErrorPublishMenus from './BannerErrorPublishMenus';
import { getScopeBasedTitle, getScopeBasedButtonLabel, relocatePage } from './utils';
import useStyles from './styles';

// Custom Icons

// Menu Scope

/**
 * TopNavigationBarStores Component
 * Manages navigation between store and organization contexts
 * Handles draft mode switching and scope selection for menu management
 *
 * @returns {React.ReactElement} Top navigation bar component
 */
function TopNavigationBarStores(): React.ReactElement {
  const classes = useStyles();
  const { t } = useTranslation(['menuMaker']);
  const location = useLocation();
  const { logEvent } = useAmplitude();

  const { data: user, remove: resetUser } = useUser();

  const { flags } = useFeatures();
  const { organization } = useOrganization();
  const { modeDraft, setModeDraft, isPublishMenuError } = useSidebarLayout();
  const { licenseStatus } = useLicenseState();
  const { drawerIsDirty } = useMenuMakerDrawerState();
  const {
    storeState: { stores = [], storeDraft, selected: storeSelected },
    setStore,
    storeBackup,
    setStoreBackup,
  } = useUserStore();
  const {
    currentNavigation: typeNavigation,
    setNavigation,
    reloadStoreOnClose,
    setReloadStoreOnClose,
    changeLocationState,
    setChangeLocationState,
    goToMenuDraft,
    setGoToMenuDraft,
  } = useNavigation();

  // Local state
  const [wasClicked, setWasClicked] = useState<boolean>(false);
  const [elMenu, setMenu] = useState<HTMLElement | null>(null);
  const [stateChangeStoreBackup, setStateChangeStoreBackup] = useState<string>('');

  // Feature flags and path checks
  const { enableValidateItemsAvailability } = getValidateItemsAvailabilityFlags(flags);
  const isPathMenu = location.pathname.startsWith(MENU_ROUTES.ROOT);
  // Check if we're exactly on the scope selection page
  const isOnScopeSelectionPage = location.pathname === MENU_ROUTES.ROOT;

  // 🔄 SYNC: Ensure selectedScope and typeNavigation stay synchronized
  useMenuScopeSync();

  // Show organization button only if user has admin permissions, is in draft mode, and NOT on scope selection page
  const showButtonOrganizationMenu = storeDraft && isPathMenu && !isOnScopeSelectionPage;

  // Navbar scope styles - now based on selected menu scope from localStorage
  const { navbarCustomStyle, NavbarIconComponent, iconStyle } = useNavbarScopeStyles();

  // Create the custom icon element if component is available
  const navbarCustomIcon = useMemo(() => {
    return NavbarIconComponent ? <NavbarIconComponent style={iconStyle} /> : null;
  }, [NavbarIconComponent, iconStyle]);

  // Initialize navigation state for draft stores
  useEffect(() => {
    if (storeSelected?.isDraft) {
      setNavigation(NavigationType.STORE_DRAFT);
      setModeDraft(true);
    }
  }, [storeSelected?.isDraft, setNavigation, setModeDraft]);

  // Handle navigation state changes when closing drawers
  useEffect(() => {
    if (!storeSelected?.isDraft && !goToMenuDraft && changeLocationState && reloadStoreOnClose) {
      setReloadStoreOnClose(false);
      setChangeLocationState(false);
      handleReloadInformationStore(stateChangeStoreBackup);
    }

    if (!storeSelected?.isDraft && goToMenuDraft && changeLocationState && reloadStoreOnClose) {
      setReloadStoreOnClose(false);
      setChangeLocationState(false);
      handleLoadNavigationStoreDraft();
    }

    if (storeSelected?.isDraft && changeLocationState && reloadStoreOnClose) {
      setReloadStoreOnClose(false);
      setChangeLocationState(false);
      handleLoadNavigationStore();
    }
  }, [changeLocationState, reloadStoreOnClose, storeSelected?.isDraft, goToMenuDraft, stateChangeStoreBackup]);

  /**
   * Dynamic title based on selected scope and context
   * Uses new scope-based formatting: "Store: [name]" or "Organization - [name]"
   */
  const title = useMemo(() => {
    const isOrganizationMode =
      typeNavigation === NavigationType.ORGANIZATION || typeNavigation === NavigationType.STORE_DRAFT;
    return getScopeBasedTitle(isOrganizationMode, t, organization?.name, storeSelected?.name);
  }, [typeNavigation, t, organization?.name, storeSelected?.name]);

  /**
   * Dynamic button label based on selected scope
   * Shows opposite action: "Change to organization" when in store, "Change to store" when in organization
   */
  const buttonLabel = useMemo(() => {
    const isCurrentlyInStoreMode = typeNavigation === NavigationType.STORE;
    return getScopeBasedButtonLabel(isCurrentlyInStoreMode);
  }, [typeNavigation]);

  /**
   * Show chevron dropdown when in store mode with multiple stores
   */
  const showChevron = useMemo(() => {
    return typeNavigation === NavigationType.STORE && stores.length > 1;
  }, [typeNavigation, stores]);

  /**
   * Handles chevron click to show/hide store list
   *
   * @param {HTMLElement} element - The clicked element
   */
  function handleChevronClick(element: HTMLElement): void {
    setMenu(element);
    setWasClicked(!wasClicked);
  }

  /**
   * Closes the store selection list
   */
  function onCloseList(): void {
    setWasClicked(false);
  }

  /**
   * Handles store selection from dropdown
   *
   * @param {string} values - Selected store UUID
   */
  function handleOnSelectedStore(values: string): void {
    if (storeSelected?.uuid !== values) {
      relocatePage(location, history.replace);
      onCloseList();
      if (drawerIsDirty) {
        setChangeLocationState(true);
        return setStateChangeStoreBackup(values);
      }
      handleReloadInformationStore(values);
    } else {
      onCloseList();
    }
  }

  /**
   * Reloads store information and updates analytics
   *
   * @param {string} values - Store UUID to load
   */
  const handleReloadInformationStore = (values: string): void => {
    const changeStore = stores.find((store: StoreProps) => store.uuid === values);
    setStoreBackup(changeStore);

    logger.updateGlobalContext({
      'store.uuid': changeStore?.uuid,
      'store.name': changeStore?.name,
    });

    setUserPropertyRequest({
      email: user?.email,
      store_name: changeStore?.name,
      store_id: changeStore?.uuid,
      organization_id: organization?.uuid,
      organization_name: organization?.name,
    });
    logEvent('@event.$store.viewStore', '', {
      store: changeStore?.uuid,
      isDraft: changeStore?.isDraft,
      license: licenseStatus,
    });

    setTimeout(() => {
      window.location.reload();
      setStore(changeStore);
    }, 100);
  };

  /**
   * Handles navigation to regular store mode
   */
  function handleLoadNavigationStore(): void {
    const changeStore = storeBackup ?? stores[0];
    setModeDraft(false);
    setStore(changeStore);
    setNavigation(NavigationType.STORE);
    logEvent('@event.$store.viewStore', '', {
      store: changeStore?.uuid,
      isDraft: changeStore?.isDraft,
      license: licenseStatus,
    });
    resetUser();
  }

  /**
   * Handles navigation to store draft mode
   */
  function handleLoadNavigationStoreDraft(): void {
    setStoreBackup(storeSelected);
    setModeDraft(true);
    setNavigation(NavigationType.STORE_DRAFT);
    setStore(storeDraft);
    logEvent('@event.$store.viewStore', '', {
      store: storeDraft?.uuid,
      isDraft: storeDraft?.isDraft,
      license: licenseStatus,
    });
    resetUser();
  }

  /**
   * Handles navigation to store draft menu
   * Manages dirty state and navigation transitions
   */
  function handleGoToStoreDraftMenu(): void {
    history.replace({ search: '' });

    if (typeNavigation === NavigationType.STORE_DRAFT) {
      if (drawerIsDirty) {
        return setChangeLocationState(true);
      }
      return handleLoadNavigationStore();
    }

    if (typeNavigation === NavigationType.STORE) {
      if (drawerIsDirty) {
        setGoToMenuDraft(true);
        return setChangeLocationState(true);
      }

      logEvent('@event.$store.organizationSwitched', '', {
        organization_uuid: organization?.uuid,
        module_from: amplitudeConfig['@@section'].menu,
      });
      handleLoadNavigationStoreDraft();
    }
  }

  // Configure menu scope navigation hook
  const { handleScopeNavigation } = useMenuScopeNavigation({
    onNavigateToStoreDraftMenu: handleGoToStoreDraftMenu,
  });

  /**
   * Dynamic button icon based on selected scope
   * Shows the OPPOSITE icon since the button represents changing to the other scope
   * If in Store scope → Show Organization icon (because button says "Change to Organization")
   * If in Organization scope → Show Store icon (because button says "Change to Store")
   */
  const buttonIcon = useMemo(() => {
    if (!NavbarIconComponent) return null;

    // Return the OPPOSITE icon based on current navigation type
    const isCurrentlyInStoreMode = typeNavigation === NavigationType.STORE;
    const OppositeIconComponent = isCurrentlyInStoreMode ? CustomOrganizationIcon : CustomStoreIcon;

    return <OppositeIconComponent style={iconStyle} />;
  }, [typeNavigation, iconStyle, NavbarIconComponent]);

  return (
    <>
      <TopNavigationBar
        chevronDown={wasClicked}
        customIcon={navbarCustomIcon}
        customStyle={navbarCustomStyle}
        onClickChevron={handleChevronClick}
        showChevron={showChevron}
        title={title}
        type={typeNavigation}
      >
        {showButtonOrganizationMenu && (
          <Box className={classes.buttonSection}>
            <Button customIcon={buttonIcon} onClick={handleScopeNavigation} style={{ color: iconStyle.color }}>
              <Box className={classes.labelBtnOrganization}>{t(buttonLabel)}</Box>
            </Button>
          </Box>
        )}
      </TopNavigationBar>

      {/* {modeDraft && isPathMenu && <Box className={classes.messageModeDraft}>{t('menuMaker:messages.modeDraft')}</Box>} */}

      {enableValidateItemsAvailability && isPublishMenuError && !modeDraft && <BannerErrorPublishMenus t={t} />}

      <TopNavigationBar.List anchorEl={elMenu} onClose={onCloseList} open={wasClicked}>
        {stores.map((store: StoreProps) => (
          <TopNavigationBar.ListItem
            key={store.uuid}
            onClick={handleOnSelectedStore}
            text={store.name}
            value={store.uuid}
          />
        ))}
      </TopNavigationBar.List>
    </>
  );
}

export default TopNavigationBarStores;
